import { Module } from "vuex";
import { IRootState } from "@monorepo/inventory/src/store";
import { QUERY_PATH } from "@monorepo/utils/src/api/queryPath";
import { checkExistLibrary, encodeQueryData, getFullPath, getQuery } from "@monorepo/utils/src/api/utils";
import { convertFiltersCustomToApi } from "@monorepo/inventory/src/views/AKView/utils/convertFiltersToApi";
import { IAKElement } from "@monorepo/inventory/src/views/AKView/types/element";
import { getSelectedIds } from "@monorepo/utils/src/utils/getSelectedIds";
import { ITableFiltersObject } from "@monorepo/utils/src/store/types/tableFiltersObject";
import { convertFiltersToApi, IConvertFiltersToApiResponse } from "@monorepo/utils/src/api/convertFiltersToApi";
import { mutations as baseMutations } from "@monorepo/utils/src/store/modules/mutations";
import { actions as baseActions } from "@monorepo/utils/src/store/modules/actions";
import { getters as baseGetters } from "@monorepo/utils/src/store/modules/getters";
import { ISection, Sections } from "@monorepo/utils/src/types/cellsSections";
import { ISearchTemplate } from "@monorepo/utils/src/types/ISearchTemplate";
import { SORT_TYPE } from "@monorepo/utils/src/types/sortTypes";
import axios from "axios";
import { showNotification } from "@monorepo/utils/src/eventBus/utils/showNotification";
import { NOTIFICATION_STATUS } from "@monorepo/utils/src/eventBus/types/notification";

interface ITableState {
  filters: ITableFiltersObject;
  totalLength: number;
  section?: Sections;
  data: IAKElement[];
  sectionCells: ISection;
  isOpenFilters: boolean;
  autorefresh: boolean;
  searchTemplates: ISearchTemplate[];
  cells: string[];
  infiniteId: string;
  libraries: {
    oiks: string[];
    statuses: string[];
    types: string[];
  };
  isTableLoading: boolean;
  isLoadingToggleFilters: boolean;
  isLoadingChangeAutorefresh: boolean;
}

const defaultFilters = (): ITableFiltersObject => ({
  sort: {
    creationDate: SORT_TYPE.DESC,
  },
  openedId: null,
  isSelectAll: false,
  selectedIds: {},
  fieldFilters: {},
  initMessagesLength: 50,
});

const getDefaultState = (): ITableState => ({
  filters: defaultFilters(),
  infiniteId: new Date().toString(),
  sectionCells: {} as ISection,
  totalLength: 0,
  searchTemplates: [],
  cells: [],
  section: Sections.AK,
  data: [],
  isOpenFilters: false,
  autorefresh: false,
  libraries: {
    oiks: [],
    statuses: [],
    types: [],
  },
  isTableLoading: false,
  isLoadingToggleFilters: false,
  isLoadingChangeAutorefresh: false,
});

const resultExportPath = (payload: "PDF" | "CSV" | "XLSX") => {
  switch (payload) {
    case "CSV":
      return QUERY_PATH.GET_AK_CSV_LIST;
    case "XLSX":
      return QUERY_PATH.GET_AK_XLS_LIST;
    default:
      return QUERY_PATH.GET_AK_PDF_LIST;
  }
};

export const akView: Module<ITableState, IRootState> = {
  namespaced: true,
  state: getDefaultState,
  mutations: {
    ...baseMutations,
    addSort(state: ITableState, payload: Record<string, SORT_TYPE>) {
      state.filters.sort = { ...payload };
    },
    setFilters(state: ITableState, payload: ReturnType<typeof defaultFilters>) {
      state.filters = payload;
    },
    resetState(state: ITableState) {
      Object.assign(state, getDefaultState());
    },
    addOiksLib(state: ITableState, payload: string[]) {
      state.libraries.oiks = payload || [];
    },
    addStatusesLib(state: ITableState, payload: string[]) {
      state.libraries.statuses = payload || [];
    },
    addTypesLib(state: ITableState, payload: string[]) {
      state.libraries.types = payload || [];
    },
    clearFilters(state: ITableState) {
      state.filters = defaultFilters();
    },
    addData(
      state: ITableState,
      payload: {
        data: IAKElement[];
        totalLength: number;
        isReset: boolean;
      }
    ) {
      state.data = payload.isReset ? payload.data : state.data.concat(payload.data);
      state.totalLength = payload.totalLength;
    },
    setTableLoading(state: ITableState, payload: boolean) {
      state.isTableLoading = payload;
    },
  },
  actions: {
    ...baseActions,
    async getOiks({ commit, state }) {
      checkExistLibrary(state.libraries.oiks, async () => {
        const { data } = await getQuery<string[]>(QUERY_PATH.GET_OIKS_LIB);
        commit("addOiksLib", data);
      });
    },
    async getStatuses({ commit, state }) {
      checkExistLibrary(state.libraries.statuses, async () => {
        const { data } = await getQuery<string[]>(QUERY_PATH.GET_AK_STATUSES_LIB);
        commit("addStatusesLib", data);
      });
    },
    async getTypes({ commit, state }) {
      checkExistLibrary(state.libraries.types, async () => {
        const { data } = await getQuery<string[]>(QUERY_PATH.GET_AK_TYPES_LIB);
        if (data !== null) {
          commit("addTypesLib", data);
        }
      });
    },
    async renewalAKSignatureQuery(info, id: string): Promise<boolean> {
      try {
        await axios.post(getFullPath(`${QUERY_PATH.GET_RENEWAL_AK_RECEIPT}/${id}`));
        return true;
      } catch (e) {
        showNotification("Ошибка продления юридической значимости");
        return false;
      }
    },
    async getEventList({ commit, state, rootGetters }) {
      if (rootGetters["auth/isShowAnimation"]) {
        commit("setTableLoading", true);
      }
      const params = convertFiltersToApi(state.filters, state.data.length, convertFiltersCustomToApi);
      const {
        data,
        total: totalLength,
        error,
      } = await getQuery<IAKElement[]>(QUERY_PATH.GET_AK_LIST, params).finally(() => {
        commit("setTableLoading", false);
      });
      if (data !== null) {
        commit("addData", { data: data || [], totalLength: totalLength || 0 });
        return { data: state.data || [], totalLength: totalLength || 0 };
      }
      return { data: null, error };
    },
    async getAKElement(info, payload: string) {
      const { data } = await getQuery<IAKElement>(`${QUERY_PATH.GET_AK_LIST}/card/${payload}`);
      return data || {};
    },
    async getSignatures(info, payload: string) {
      const { data } = await getQuery<any>(`${QUERY_PATH.GET_DIGITAL_SIGNATURES}/${payload}`);
      return data || {};
    },
    async refreshEventList({ commit, state }) {
      const params = convertFiltersToApi(state.filters, state.data.length, convertFiltersCustomToApi);
      const { data, total: totalLength } = await getQuery<IAKElement[]>(QUERY_PATH.GET_AK_LIST, { ...params, offset: 0 });

      if (data !== null) {
        commit("addData", { data: data || [], totalLength: totalLength || 0, isReset: true });
      }
      return { data: null };
    },
    async getExportElementData({ dispatch }, payload: string) {
      dispatch("app/openNewWindow", getFullPath(`${QUERY_PATH.GET_AK_EXPORT}/${payload}/pdf`), {
        root: true,
      });
    },
    async getExportData({ state, dispatch, commit }, payload: "CSV" | "PDF" | "XLSX") {
      let params: IConvertFiltersToApiResponse | { ids: string[] };
      const selectedKeys = state.filters.selectedIds ? getSelectedIds(state.filters.selectedIds) : [];
      if (selectedKeys.length && !state.filters.isSelectAll) {
        params = {
          ids: selectedKeys,
        };
      } else {
        params = convertFiltersToApi(state.filters, state.data.length, convertFiltersCustomToApi, true);
      }
      const queryParams = encodeQueryData(params);
      if (payload !== "PDF") {
        dispatch("app/openNewWindow", getFullPath(resultExportPath(payload), queryParams), {
          root: true,
        });
      } else {
        commit("app/setIsExportFileLoading", true, {
          root: true,
        });
        const { data } = await getQuery<void>(QUERY_PATH.GET_AK_PDF_LIST, params);
        if (!data) {
          commit("app/setIsExportFileLoading", false, {
            root: true,
          });
        }
      }
    },
    async recognizeEadContent(info, id: string) {
      try {
        await getQuery(getFullPath(`${QUERY_PATH.GET_RECOGNIZED_CONTENT}/${id}`)).then(({ error }) => {
          if (!error) {
            showNotification("Содержимое документов АК распознано", NOTIFICATION_STATUS.SUCCESS);
          }
        });
        return true;
      } catch (e) {
        showNotification("Ошибка распознавания содержимого документов АК");
        return false;
      }
    },
  },
  getters: {
    ...baseGetters,
    oiksLibrary(state: ITableState) {
      return state.libraries.oiks;
    },
    statusesLibrary(state: ITableState) {
      return state.libraries.statuses;
    },
    typesLibrary(state: ITableState) {
      return state.libraries.types;
    },
    isTableLoading(state: ITableState) {
      return state.isTableLoading;
    },
  },
};
